import { useSelector } from 'react-redux'
import { store, IStore } from '../../../store'
import { AppTypes } from '../../../reducers/app.reducer'
import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { debounceTime } from 'rxjs'
import { unreadCountUpdated } from '../../chatComponent/chat'
import { ModalType, openModalTask } from '../../modalComponent/modalComponent'
import { open } from '@src/reducers/authModal.reducer'
import { useTranslation } from 'react-i18next'
import { AttentionPoint } from './AttentionPoint'
import HomeIcon from '@src/components/icons/home'
import { SpecialBonusAttention } from '../SpecialBonusAttention'
import { MobileWallet } from './MobileWallet'
import { isUserLoggedIn } from '@src/utils/functions'
import { useDispatch } from 'react-redux'
import { useBounties } from '@src/context/BountiesContext'

import type { User } from '@src/stores/auth/user'
import Search from '@src/components/icons/search'

interface Props {
  me?: User
}

const MobileNavBar = ({ me }: Props) => {
  const isNotAnonymousUser = null === me?.anonymousHash
  const location = useLocation()
  const { t } = useTranslation()
  const chatOpened = useSelector((state: IStore) => state.app.chatOpened)
  const menuOpened = useSelector((state: IStore) => state.app.menuOpened)
  const toggleSearch = () => store.dispatch({ type: AppTypes.TOGGLE_SEARCH })
  const closeMenu = () => store.dispatch({ type: AppTypes.CLOSE_MENU })
  const openMenu = () => store.dispatch({ type: AppTypes.OPEN_MENU })
  const openChat = () => store.dispatch({ type: AppTypes.OPEN_CHAT })
  const closeChat = () => store.dispatch({ type: AppTypes.CLOSE_CHAT })
  const isLoggedIn = isUserLoggedIn()
  const { setBountyModalVisible } = useBounties()
  const dispatch = useDispatch()

  const [activeButton, setActiveButton] = useState('')
  const [unread, setUnread] = useState(0)

  useEffect(() => {
    const s = unreadCountUpdated.pipe(debounceTime(50)).subscribe((count) => {
      setUnread(count)
    })
    return () => {
      s.unsubscribe()
    }
  }, [])

  useEffect(() => {
    setActiveButton(location.pathname)
  }, [location.pathname])

  const toggleChat = () => {
    if (chatOpened) {
      closeChat()
      setActiveButton(location.pathname)
    } else {
      if (menuOpened) closeMenu()
      openChat()
      setActiveButton('chat')

      setTimeout(() => {
        localStorage.setItem('last_read_time', Date.now().toString())
        setUnread(0)
      })
    }
  }

  const toggleBrowse = () => {
    if (menuOpened) {
      closeMenu()
      setActiveButton(location.pathname)
    } else {
      if (chatOpened) closeChat()
      openMenu()
      setActiveButton('browse')
    }
  }

  const handleLinkClick = (targetRoute) => {
    if (menuOpened) closeMenu()
    if (chatOpened) closeChat()
    setActiveButton(targetRoute)
  }

  const mainButtonClickHandler = () => {
    isNotAnonymousUser ? setBountyModalVisible(true) : openModalTask.next({ type: ModalType.wallet })
  }

  const clickHandler = (callback) => {
    if (!isLoggedIn) {
      return () => dispatch(open({ tab: 'Login' }))
    } else {
      return callback
    }
  }

  const searchClickHandler = () => {
    toggleSearch()
    closeMenu()
    closeChat()
    setActiveButton(location.pathname)
  }

  return (
    <>
      <div className="fixed bottom-0 left-0 w-full h-60 bg-navMenuDark shadow-top" style={{ zIndex: 60 }}>
        <div className="grid h-full max-w-lg grid-cols-5 mx-auto font-medium">
          <button
            onClick={toggleBrowse}
            className={`inline-flex flex-col items-center justify-center px-5 gap-5 ${
              activeButton === 'browse' ? 'text-topaz' : 'text-whiteSmoke'
            }`}
          >
            <div className="relative">
              <BrowseIcon color={activeButton === 'browse' ? '#FFCE73' : '#ECECEC'} />
              <SpecialBonusAttention me={me} />
            </div>
            <span className="text-xs">Browse</span>
          </button>
          <Link
            to="/"
            onClick={() => handleLinkClick('/')}
            className={`inline-flex flex-col items-center justify-center px-5 gap-7 ${
              activeButton === '/' ? 'text-topaz' : 'text-whiteSmoke'
            }`}
          >
            <HomeIcon width="22" height="22" />
            <span className="text-xs">{t('menu.home', 'Home')}</span>
          </Link>
          <MobileWallet mainButtonClickHandler={clickHandler(mainButtonClickHandler)} me={me} />
          <button
            onClick={searchClickHandler}
            className={`inline-flex flex-col items-center justify-center px-5 gap-5 text-whiteSmoke`}
          >
            <div className="relative">
              <Search color="#ECECEC" />
            </div>
            <span className="text-xs">{t('globalSearchComponent.search', 'Search')}</span>
          </button>
          <button
            onClick={toggleChat}
            className={`inline-flex flex-col items-center justify-center px-5 gap-7 relative ${
              activeButton === 'chat' ? 'text-topaz' : 'text-whiteSmoke'
            }`}
          >
            <div className="relative">
              <ChatIcon color={activeButton === 'chat' ? '#FFCE73' : '#ECECEC'} />
              {unread > 0 && <AttentionPoint type="chat" />}
            </div>
            <span className="text-xs">{t('menu.chat', 'Chat')}</span>
          </button>
        </div>
      </div>
    </>
  )
}

export default MobileNavBar

const BrowseIcon = ({ color = 'white', ...props }) => (
  <svg width="24" height="24" viewBox="0 0 20 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="13" height="2.6" rx="1.3" fill={color} />
    <rect x="6.68579" y="10.4" width="13" height="2.6" rx="1.3" fill={color} />
    <rect y="5.20001" width="19.3143" height="2.6" rx="1.3" fill={color} />
  </svg>
)

const ChatIcon = ({ color = 'white', ...props }) => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M1.58244 14.4004L1.58245 14.4004L1.58063 14.3969C0.0373068 11.4461 0.153672 7.90415 1.88741 5.06064C3.62105 2.21727 6.71915 0.48689 10.054 0.500075C14.001 0.529336 17.5198 2.98792 18.8992 6.67907C20.2788 10.3703 19.2329 14.5289 16.2698 17.1318C13.3065 19.7348 9.04101 20.2415 5.54848 18.4051C5.53858 18.3999 5.52853 18.3951 5.51834 18.3905C5.50575 18.3836 5.48687 18.373 5.46095 18.3581C5.42768 18.3391 5.38923 18.3168 5.34571 18.2916C5.31122 18.2717 5.27355 18.2499 5.23274 18.2264C5.05869 18.1261 4.83233 17.9972 4.67433 17.9211C4.66522 17.9167 4.65597 17.9126 4.64661 17.9087C4.24144 17.7429 3.79265 17.716 3.37056 17.8323C3.35828 17.8357 3.34614 17.8395 3.33415 17.8438C2.64396 18.092 1.93903 18.2971 1.22344 18.4582C1.22151 18.4416 1.21993 18.4218 1.21894 18.3984C1.40224 17.6511 1.63007 16.9155 1.90122 16.1953C1.90471 16.186 1.90792 16.1766 1.91086 16.1672C2.05315 15.7082 2.00635 15.2117 1.78101 14.7875L1.58244 14.4004ZM5.26413 10.0001C5.26413 9.98615 5.27521 9.97626 5.28717 9.97626C5.29912 9.97626 5.3102 9.98615 5.3102 10.0001C5.3102 10.014 5.29912 10.0239 5.28717 10.0239C5.27521 10.0239 5.26413 10.014 5.26413 10.0001ZM5.28717 10.9763C5.82596 10.9763 6.26413 10.54 6.26413 10.0001C6.26413 9.46017 5.82596 9.02388 5.28717 9.02388C4.74837 9.02388 4.3102 9.46017 4.3102 10.0001C4.3102 10.54 4.74837 10.9763 5.28717 10.9763ZM10.0338 10.0001C10.0338 9.98615 10.0449 9.97626 10.0568 9.97626C10.0688 9.97626 10.0799 9.98615 10.0799 10.0001C10.0799 10.014 10.0688 10.0239 10.0568 10.0239C10.0449 10.0239 10.0338 10.014 10.0338 10.0001ZM10.0568 10.9763C10.5956 10.9763 11.0338 10.54 11.0338 10.0001C11.0338 9.46016 10.5956 9.02388 10.0568 9.02388C9.51802 9.02388 9.07985 9.46017 9.07985 10.0001C9.07985 10.54 9.51802 10.9763 10.0568 10.9763ZM14.8034 10.0001C14.8034 9.98615 14.8145 9.97626 14.8265 9.97626C14.8384 9.97626 14.8495 9.98615 14.8495 10.0001C14.8495 10.014 14.8384 10.0239 14.8265 10.0239C14.8145 10.0239 14.8034 10.014 14.8034 10.0001ZM14.8265 10.9763C15.3653 10.9763 15.8034 10.54 15.8034 10.0001C15.8034 9.46016 15.3653 9.02388 14.8265 9.02388C14.2877 9.02388 13.8495 9.46017 13.8495 10.0001C13.8495 10.54 14.2877 10.9763 14.8265 10.9763Z"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
